"use client";

import clsx from "clsx";
import { useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { type PointerEvent as ReactPointerEvent, useRef, useState } from "react";

const ARROW_PATHS = {
  next: {
    default: "M0.5 2.5L6 11L0.5 19.5",
    hover: "M1 2L6 11L1 20",
    press: "M0.500244 1L4.00024 11L0.500244 21",
  },
  previous: {
    default: "M6.5 2.5L1 11L6.5 19.5",
    hover: "M6 2L1 11L6 20",
    press: "M6.499756 1L2.99976 11L6.499756 21",
  },
} as const;
const ARROW_EASE = [0.22, 1, 0.36, 1] as const;
const ARROW_TRANSITION = {
  opacity: { duration: 0.34, ease: ARROW_EASE },
  scale: { duration: 0.34, ease: ARROW_EASE },
  x: { duration: 0.34, ease: ARROW_EASE },
};
const ARROW_EXIT_SCALE = 0.8;
const ENTER_OFFSET_X = 15;
const EXIT_OFFSET_X = 15;
const PRESS_OFFSET_X = 2;

type AnimatedChevronProps = {
  isPrevious: boolean;
  onExitComplete: () => void;
  pathVariant: ArrowPathVariant;
  phase: ArrowPhase;
  shouldAnimateIn: boolean;
};

type ArrowInstance = {
  id: number;
  pathVariant: ArrowPathVariant;
  phase: ArrowPhase;
  shouldAnimateIn: boolean;
};

type ArrowPathVariant = "default" | "hover" | "press";
type ArrowPhase = "current" | "exiting";

const INITIAL_ARROW: ArrowInstance = {
  id: 0,
  pathVariant: "default",
  phase: "current",
  shouldAnimateIn: false,
};

type NavArrowButtonProps = {
  ariaLabel?: string;
  className?: string;
  direction: -1 | 1;
  disabled?: boolean;
  onClick?: () => void;
};

export function NavArrowButton({ ariaLabel, className, direction, disabled = false, onClick }: NavArrowButtonProps) {
  const isPrevious = direction < 0;
  const tGlobal = useTranslations("ClientKeys.Globals");
  const nextArrowIdRef = useRef(1);
  const [arrows, setArrows] = useState<ArrowInstance[]>(() => [INITIAL_ARROW]);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const reducedMotion = useReducedMotion();
  const currentPathVariant = resolveCurrentPathVariant(isHovered, isPressed);

  function spawnArrow(pathVariant: ArrowPathVariant) {
    if (reducedMotion) return;

    setArrows(currentArrows => {
      let demotedCurrentArrow = false;
      const nextArrows = currentArrows.map(arrow => {
        if (!demotedCurrentArrow && arrow.phase === "current") {
          demotedCurrentArrow = true;
          return { ...arrow, pathVariant, phase: "exiting" as const };
        }

        return arrow;
      });

      return [
        ...nextArrows,
        {
          id: nextArrowIdRef.current++,
          pathVariant,
          phase: "current",
          shouldAnimateIn: true,
        },
      ];
    });
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    event.stopPropagation();

    if (disabled) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    setIsPressed(true);
  }

  function handlePointerEnter(event: ReactPointerEvent<HTMLButtonElement>) {
    if (disabled || event.pointerType === "touch") return;

    setIsHovered(true);
  }

  function handlePointerEnd() {
    setIsPressed(false);
  }

  function handlePointerLeave(event: ReactPointerEvent<HTMLButtonElement>) {
    if (event.pointerType === "touch") return;

    setIsHovered(false);
    setIsPressed(false);
  }

  function handleClick() {
    if (!disabled) {
      spawnArrow(resolveCurrentPathVariant(isHovered, false));
    }

    onClick?.();
  }

  function handleExitComplete(id: number) {
    setArrows(currentArrows => currentArrows.filter(arrow => arrow.id !== id));
  }

  return (
    <button
      aria-label={ariaLabel ?? (isPrevious ? tGlobal("previous") : tGlobal("next"))}
      className={clsx(
        "tw-px-4 tw-py-2 tw-outline-none [&_path]:tw-stroke-black focus-visible:tw-outline-focus",
        disabled ? "tw-cursor-default tw-opacity-40" : "tw-cursor-pointer",
        className,
      )}
      disabled={disabled}
      onClick={handleClick}
      onLostPointerCapture={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onPointerDown={handlePointerDown}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerUp={handlePointerEnd}
      type='button'
    >
      <span aria-hidden='true' className='tw-relative tw-block tw-h-[22px] tw-w-[7px] tw-overflow-visible'>
        {arrows.map(arrow => (
          <AnimatedChevron
            isPrevious={isPrevious}
            key={arrow.id}
            onExitComplete={() => handleExitComplete(arrow.id)}
            pathVariant={arrow.phase === "current" ? currentPathVariant : arrow.pathVariant}
            phase={arrow.phase}
            shouldAnimateIn={arrow.shouldAnimateIn}
          />
        ))}
      </span>
    </button>
  );
}

function AnimatedChevron({ isPrevious, onExitComplete, pathVariant, phase, shouldAnimateIn }: AnimatedChevronProps) {
  const path = getArrowPath(isPrevious, pathVariant);
  const transformOrigin = isPrevious ? "0% 50%" : "100% 50%";
  const pressOffsetX = isPrevious ? PRESS_OFFSET_X : -PRESS_OFFSET_X;
  const animateTarget =
    phase === "current"
      ? { opacity: 1, scale: 1, x: pathVariant === "press" ? pressOffsetX : 0 }
      : { opacity: 0, scale: ARROW_EXIT_SCALE, x: isPrevious ? -EXIT_OFFSET_X : EXIT_OFFSET_X };
  const initialState = shouldAnimateIn
    ? { opacity: 0, scale: 1, x: isPrevious ? ENTER_OFFSET_X : -ENTER_OFFSET_X }
    : false;

  return (
    <m.svg
      animate={animateTarget}
      className='tw-absolute tw-inset-0 tw-overflow-visible'
      fill='none'
      height='22'
      initial={initialState}
      onAnimationComplete={phase === "exiting" ? onExitComplete : undefined}
      style={{ transformOrigin }}
      transition={ARROW_TRANSITION}
      viewBox='0 0 7 22'
      width='7'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path d={path} stroke='black' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' />
    </m.svg>
  );
}

function getArrowPath(isPrevious: boolean, pathVariant: ArrowPathVariant) {
  return ARROW_PATHS[isPrevious ? "previous" : "next"][pathVariant];
}

function resolveCurrentPathVariant(isHovered: boolean, isPressed: boolean) {
  if (isPressed) return "press";
  if (isHovered) return "hover";

  return "default";
}
